import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Tooltip, Modal, Button, Slider, IconButton } from '@mui/material';
import { InfoOutlinedIcon, PersonOutlineOutlinedIcon, CloseIcon, LockOutlinedIcon } from 'icons/materialIcons';

import Cropper from 'react-easy-crop';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils'; // Helper function to get the cropped image
import { useCardContext } from 'contexts/CardContext'; // Import the CardContext
import { useParams } from 'react-router-dom';
import { getCardById } from 'apis/cardApi'; // Adjust the import path as needed
import { validateUUID } from 'utils/validator';

const TeamProfilePicture = ({
    onCroppedImage,
    cardLayout,
    onDeleteProfilePicture,
    cardDesign,
    isRestricted = false,
    canRequestChange = false,
    onRestrictionNotification,
    initialImageUrl // For templates
}) => {

    // Add this ref to track the previous layout
    const prevLayoutRef = useRef(cardLayout);

    const [image, setImage] = useState(null);
    const [open, setOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [cardDetails, setCardDetails] = useState(null);
    const [loading, setLoading] = useState(true); // State to handle loading state

    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null); // Store the cropped image URL
    const [objectUrl, setObjectUrl] = useState(null);
    const { cardState, setCardProfilePictureUrl } = useCardContext();
    const { cardId } = useParams(); // Extracts the cardId from the URL
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);

    const cardProfilePictureUrl = cardState[cardId]?.cardProfilePictureUrl;
    const [inputDisabled, setInputDisabled] = useState(false); // State to disable input

    // Add this function near your other state declarations
    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const widthRatio = containerWidth / mediaWidth;
        const heightRatio = containerHeight / mediaHeight;
        return Math.min(widthRatio, heightRatio);
    }, []);

    // Add this callback
    const onMediaLoaded = useCallback((mediaSize) => {
        const initialZoom = calculateInitialZoom(mediaSize.width, mediaSize.height);
        setZoom(initialZoom);
        setCrop({ x: 0, y: 0 });
    }, [calculateInitialZoom]);

    useEffect(() => {
        if (cardDetails?.card && !validateUUID(cardId)) { // Ensure cardDetails.card exists
            setProfilePictureUrl(cardDetails.card.profilePictureUrl || '');
        }
    }, [cardDetails]);

    useEffect(() => {
        if (!validateUUID(cardId)) {
            // Clear local state
            setCardProfilePictureUrl(cardId, null);

            // Ensure the cleanup function is called on unmount
            return () => {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            };
        }
    }, [cardId, objectUrl]); // Include cardId and objectUrl in the dependency array

    useEffect(() => {
        const fetchCardDetails = async () => {
            try {
                const data = await getCardById(cardId);
                setCardDetails(data);
                console.log('Card Details:', data); // Log card details
            } catch (error) {
                console.error('Error fetching card details:', error);
            }
        };

        if (cardId && !validateUUID(cardId)) {
            fetchCardDetails();
        }
    }, [cardId]);

    useEffect(() => {
        if (cardDetails && !validateUUID(cardId)) {
            setCroppedImage(cardDetails.card.profilePictureUrl);
        }
    }, [cardDetails]);

    // For templates: use initialImageUrl if provided and no valid cardId
    useEffect(() => {
        if (initialImageUrl && (!cardId || validateUUID(cardId))) {
            setCroppedImage(initialImageUrl);
        }
    }, [initialImageUrl, cardId]);

    // ✅ SYNC FROM CONTEXT: Update croppedImage when context changes (e.g., after template is applied)
    // Only update if user is not actively editing (modal is closed and no image is being cropped)
    useEffect(() => {
        if (!cardId || validateUUID(cardId)) return; // Skip for new cards

        // Only sync from context if user is not actively editing
        if (!open && !image && cardProfilePictureUrl !== undefined) {
            // Normalize: treat empty string as null
            const contextProfileUrl = (cardProfilePictureUrl && cardProfilePictureUrl.trim()) ? cardProfilePictureUrl : null;

            // Only update if different to prevent unnecessary re-renders
            setCroppedImage(prev => {
                // Normalize prev value for comparison
                const normalizedPrev = (prev && prev.trim()) ? prev : null;

                // If context value is different from current, update
                if (normalizedPrev !== contextProfileUrl) {
                    return contextProfileUrl;
                }
                return prev;
            });
        }
    }, [cardId, cardProfilePictureUrl, open, image]); // Watch context changes

    // Modify your handleImageChange function
    const handleImageChange = (event) => {
        // Check if field is restricted
        if (isRestricted) {
            const message = canRequestChange
                ? 'Profile Picture is restricted by your subteam. You can submit a change request.'
                : 'Profile Picture is restricted by your subteam and cannot be updated.';
            if (onRestrictionNotification) {
                onRestrictionNotification(message);
            }
            event.target.value = '';
            return;
        }

        if (event.target.files && event.target.files[0]) {
            const file = URL.createObjectURL(event.target.files[0]);
            setObjectUrl(file);
            setImage(file);

            // Create an image object to calculate initial zoom
            const img = new Image();
            img.onload = () => {
                const initialZoom = calculateInitialZoom(img.width, img.height);
                setZoom(initialZoom);
            };
            img.src = file;

            setOpen(true);
            event.target.value = '';
        }
    };

    // Function to remove the image from Firebase Storage
    const handleRemoveImage = async () => {
        // Check if field is restricted
        if (isRestricted) {
            const message = canRequestChange
                ? 'Profile Picture is restricted by your subteam. You can submit a change request.'
                : 'Profile Picture is restricted by your subteam and cannot be updated.';
            if (onRestrictionNotification) {
                onRestrictionNotification(message);
            }
            return;
        }

        // Call the deletion method passed from the parent component
        if (onDeleteProfilePicture) {
            await onDeleteProfilePicture();
        }

        setCroppedImage(null); // Clear the cropped image
        setCardProfilePictureUrl(cardId, null); // Update context with no image
        setImage(null); // Clear the file from the state

        if (objectUrl) {
            URL.revokeObjectURL(objectUrl); // Revoke the object URL
            setObjectUrl(null);
        }

        setInputDisabled(true); // Disable input to prevent click events
        setTimeout(() => setInputDisabled(false), 0); // Re-enable input after a short delay
    };

    useEffect(() => {
        console.log("TeamProfilePictureUrl", cardProfilePictureUrl);
        console.log("cropped image", croppedImage);
        console.log("Image", image);
    }, [cardProfilePictureUrl, croppedImage, image]);

    const handleClose = () => {
        setOpen(false);
        setImage(null);
        setObjectUrl(null);
        setZoom(1); // Resetting zoom to the middle value (2)
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Watch for changes in cardLayout
    useEffect(() => {
        // Only process if we have a croppedImage AND the layout has actually changed
        if (croppedImage && cardLayout !== prevLayoutRef.current && image) {
            console.log("Layout changed from", prevLayoutRef.current, "to", cardLayout);

            // Update the ref to the current layout
            prevLayoutRef.current = cardLayout;

            // Now handle the layout change safely
            (async () => {
                try {
                    // For Card Design 5, always use rectangle shape
                    let shape = 'rectangle';
                    if (cardDesign === 'design5') {
                        shape = 'rectangle';
                    } else if (cardLayout === 'left-aligned' || cardLayout === 'center-aligned') {
                        shape = 'circle';
                    }

                    console.log("Recropping image due to layout change to", cardLayout);
                    const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, shape, `profile-${cardId}`);

                    if (onCroppedImage) {
                        onCroppedImage(croppedImageBlob);
                    }

                    // Generate a URL for the cropped image Blob
                    const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
                    setCroppedImage(croppedImageUrl);

                    // This will update the context but won't trigger the effect again
                    // because we're checking against the previous layout
                    setCardProfilePictureUrl(cardId, croppedImageUrl);
                } catch (e) {
                    console.error("Error recropping image on layout change:", e);
                }
            })();
        } else {
            // Always update the ref even if we don't process the change
            prevLayoutRef.current = cardLayout;
        }
    }, [cardLayout, croppedImage, image, croppedAreaPixels, cardId, onCroppedImage, setCardProfilePictureUrl, cardDesign]);

    // Also modify your handleSave function to not recrop if layout changes
    const handleSave = async () => {
        try {
            let shape = 'rectangle';
            // For Card Design 5, always use rectangle
            if (cardDesign === 'design5') {
                shape = 'rectangle';
            } else if (cardLayout === 'left-aligned' || cardLayout === 'center-aligned') {
                shape = 'circle';
            }

            console.log("cardLayout in profile picture step2", cardLayout);
            console.log("cardDesign in profile picture", cardDesign);
            console.log("shape", shape);

            console.log("Image before cropping:", image);
            console.log("Cropped area pixels:", croppedAreaPixels);

            const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, shape, `profile-${cardId}`);

            if (onCroppedImage) {
                onCroppedImage(croppedImageBlob);
            }

            // Generate a URL for the cropped image Blob
            const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
            console.log("croppedImageUrl", croppedImageUrl);
            setCroppedImage(croppedImageUrl);

            // Update ref to current layout to prevent re-cropping loop
            prevLayoutRef.current = cardLayout;

            // Store the URL in the context
            setCardProfilePictureUrl(cardId, croppedImageUrl);

            // Close the modal
            setOpen(false);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div>
            {/* Profile Picture */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '6px' }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#4f4f4f',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        Profile Picture
                        <Tooltip title="Ideal Dimensions: 540 px by 540 px (1:1)" arrow>
                            <InfoOutlinedIcon sx={{ fontSize: '1rem', marginLeft: 0.5 }} />
                        </Tooltip>
                    </Typography>
                    {isRestricted && (
                        <Tooltip title="This field is restricted by your subteam">
                            <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: '#999' }} />
                        </Tooltip>
                    )}
                </Box>

                <Box
                    sx={{
                        position: 'relative',
                        width: 100,
                        height: 100,
                    }}
                >
                    {/* Profile Image Container */}
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            backgroundColor: '#f2f2f2',
                            border: '1px solid #dbdbdb',
                            cursor: isRestricted ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: 1,
                            backgroundImage: croppedImage ? `url(${croppedImage})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: isRestricted ? 0.6 : 1,
                        }}
                        component={isRestricted ? 'div' : 'label'}
                    >
                        {!croppedImage && (
                            <>
                                <PersonOutlineOutlinedIcon sx={{ fontSize: '28px', color: '#828282', marginBottom: '5px' }} />
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}
                                >
                                    <span style={{ color: '#79bff7' }}>Select</span> file or drag and drop here
                                </Typography>
                            </>
                        )}

                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            disabled={isRestricted}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                top: 0,
                                left: 0,
                                opacity: 0,
                                cursor: isRestricted ? 'not-allowed' : (croppedImage ? 'default' : 'pointer'),
                                pointerEvents: (croppedImage || isRestricted) ? 'none' : 'auto',
                            }}
                        />
                    </Box>

                    {/* Close Button - Only show if not restricted */}
                    {croppedImage && !isRestricted && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage();
                            }}
                            sx={{
                                position: 'absolute',
                                top: -1,
                                right: -3,
                                color: '#828282',
                                backgroundColor: 'rgb(255, 255, 255)',
                                '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                                width: 26,
                                height: 26,
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                zIndex: 2, // Ensure it is above the input and image
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Modal for cropping image */}
            <Modal
                open={open}
                onClose={handleClose}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backdropFilter: 'blur(7px)', // Apply blur effect to the background
                    backgroundColor: 'rgba(255, 255, 255, 0.8)', // Add a semi-transparent white background for the overlay
                }}
            >
                <Box
                    sx={{
                        outline: 'none',
                        width: '90vw', // Make modal take up more width
                        maxWidth: '900px', // Limit max width for responsiveness
                        height: '90vh', // Take more height for a bigger editor
                        maxHeight: '700px', // Limit max height
                    }}
                >
                    {image && (
                        <>
                            {/* Container for Cropper */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 'calc(100% - 100px)', // Adjust the height to be large
                                    marginBottom: 2,
                                }}
                            >
                                <Cropper
                                    image={image}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    cropShape="round"
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    onMediaLoaded={onMediaLoaded}
                                    objectFit="contain"
                                    centerImage={true}
                                    showGrid={false}
                                    minZoom={0.1}
                                    maxZoom={3}
                                    restrictPosition={false}
                                />
                            </Box>

                            {/* Controls for Zoom Slider and Buttons */}
                            <Box sx={{ textAlign: 'center' }}>
                                {/* Modify just the Slider component */}
                                <Slider
                                    value={zoom}
                                    onChange={(e, newValue) => setZoom(newValue)}
                                    min={0.1}
                                    max={3}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    defaultValue={1}
                                    sx={{
                                        marginBottom: 2,
                                        width: '80%',
                                        margin: '0 auto 20px auto'
                                    }}
                                />
                                <div>
                                    <Button onClick={handleClose} variant="contained" sx={{ marginRight: 2 }}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleSave} variant="contained" color="primary">
                                        Crop
                                    </Button>
                                </div>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
};

export default TeamProfilePicture;